import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const astronautImg = "/assets/astronaut.png";
const planetImg = "/assets/planet.png";
const backgroundImg = "/assets/stars-bg.png";

const NotFound: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [showRetro, setShowRetro] = useState(false);
    const [typedLines, setTypedLines] = useState<string[]>([]);
    const [hasEasterEgg, setHasEasterEgg] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [astronautPosition, setAstronautPosition] = useState({ x: 100, y: 100 });

    const retroLines = [
        "Booting Neo-Net Protocol Stack...",
        "Pinging the void... no echo received.",
        "Negotiating with rogue packets...",
        "404 subroutine encountered a paradox.",
        "Compiling the page from stardust... FAILED!",
        "ERROR: 404 - Reality not found.",
    ];

    const retroQuotes = [
        "Sorry, the URL elves got confused.",
        "Quantum entanglement broke your link.",
        "Have we gone off the edge of the internet?",
        "Uh oh! This page fell through a wormhole.",
        "Gremlins must have rewired the internet again.",
        "Turns out Schrödinger's page is neither here nor there.",
        "Our server's out chasing squirrels — be right back.",
    ];

    // Randomly pick a mode
    useEffect(() => {
        const roll = Math.random();
        setShowRetro(roll < 0.5);
    }, []);

    // Typing effect for retro mode
    useEffect(() => {
        if (!showRetro) return;

        let index = 0;
        const interval = setInterval(() => {
            setTypedLines((prev) => [...prev, retroLines[index]]);
            index++;
            if (index >= retroLines.length) clearInterval(interval);
        }, 1000);

        return () => clearInterval(interval);
    }, [showRetro]);

    // Easter egg detection
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const dropX = e.clientX;
        const dropY = e.clientY;

        const planet = document.getElementById("planet");
        if (!planet) return;

        const rect = planet.getBoundingClientRect();

        // Looser hitbox for easy dropping
        if (
            dropX > rect.left - 50 &&
            dropX < rect.right + 50 &&
            dropY > rect.top - 50 &&
            dropY < rect.bottom + 50
        ) {
            setHasEasterEgg(true);
        }

        setDragging(false);
    };

    const handleDrag = (e: React.DragEvent<HTMLImageElement>) => {
        setAstronautPosition({ x: e.clientX, y: e.clientY });
    };

    const handleGoHome = () => navigate("/dashboard");

    // Flash animation for retro error line
    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = `
    @keyframes flash {
      from { color: #ff4f4f; }
      to { color: #0f0; }
    }

    @keyframes wiggle {
      0% { transform: rotate(0deg); }
      25% { transform: rotate(2deg); }
      50% { transform: rotate(0deg); }
      75% { transform: rotate(-2deg); }
      100% { transform: rotate(0deg); }
    }
  `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);


    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            bgcolor={showRetro ? "#000" : "transparent"}
            sx={{
                backgroundImage: showRetro ? "none" : `url(${backgroundImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                color: showRetro ? "#0f0" : theme.palette.text.primary,
                overflow: "hidden",
                position: "relative",
                flexDirection: "column",
                textAlign: "center",
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
        >
            {showRetro ? (
                <>
                    <Box sx={{ fontFamily: "monospace", maxWidth: 600, p: 2 }}>
                        {typedLines.map((line, idx) => (
                            <Typography
                                key={idx}
                                sx={{
                                    mb: 1,
                                    fontSize: idx === retroLines.length - 1 ? "1.3em" : "1em",
                                    fontWeight: idx === retroLines.length - 1 ? "bold" : "normal",
                                    animation: idx === retroLines.length - 1 ? "flash 1.2s infinite alternate" : "none",
                                }}
                            >
                                {line}
                            </Typography>
                        ))}

                        {typedLines.length === retroLines.length && (
                            <Fade in>
                                <Box mt={4}>
                                    <Typography variant="caption" fontStyle="italic">
                                        {retroQuotes[Math.floor(Math.random() * retroQuotes.length)]}
                                    </Typography>
                                    <Box mt={2}>
                                        <Button variant="contained" color="secondary" onClick={handleGoHome}>
                                            Let's get out of here
                                        </Button>
                                    </Box>
                                </Box>
                            </Fade>
                        )}
                    </Box>
                </>
            ) : (
                <>
                    <Typography variant="h2" sx={{ mb: 1 }}>
                        Lost in Space
                    </Typography>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        The page you're looking for drifted into a black hole.
                    </Typography>

                    <img
                            src={astronautImg}
                        alt="astronaut"
                        draggable
                        onDragStart={() => setDragging(true)}
                        onDrag={handleDrag}
                        style={{
                            position: "absolute",
                            left: astronautPosition.x,
                            top: astronautPosition.y,
                            width: 80,
                            height: 80,
                            cursor: "grab",
                            transition: "transform 0.2s",
                            transform: dragging ? "scale(1.1)" : "scale(1)",
                            animation: "wiggle 1.5s infinite",
                            zIndex: 10,
                        }}
                    />

                    <img
                        id="planet"
                            src={planetImg}
                        alt="planet"
                        style={{
                            width: 150,
                            position: "absolute",
                            bottom: 100,
                            right: 100,
                        }}
                    />

                    <Box mt={8}>
                        <Button variant="contained" color="primary" onClick={handleGoHome}>
                            Go Home
                        </Button>
                    </Box>

                    {hasEasterEgg && (
                        <Fade in>
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 40,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    bgcolor: "rgba(0,0,0,0.7)",
                                    p: 2,
                                    borderRadius: 2,
                                    color: "#fff",
                                    fontWeight: "bold",
                                }}
                            >
                                🪐 You discovered a wormhole! Welcome to the secret dev dimension.
                            </Box>
                        </Fade>
                    )}
                </>
            )}
        </Box>
    );
};

export default NotFound;
